import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ImageBadge from 'components/images/ImageBadge';
import { BaseModal } from 'components/modals/BaseModal';
import { Color } from 'core';
import React from 'react';
import styled from 'styled-components';

interface IProps {
  contentItem: any;
  onClose: () => void;
}

export const ContentUsageModal = (props: IProps) => {
  const { contentItem, onClose } = props;
  const courses = contentItem.courses || [];

  const getTypeDisplay = (goalType: string) => {
    const typeMap: { [key: string]: string } = {
      set: 'Learning Set',
      survey: 'Survey',
      assessment: 'Assessment',
      scorm: 'SCORM',
    };
    return typeMap[goalType] || goalType;
  };

  const getCourseCountText = (courses: any[]) => {
    const count = courses?.length || 0;
    if (count === 0) return 'No courses';
    if (count === 1) return '1 course';
    return `${count} courses`;
  };

  return (
    <BaseModal title="Content Usage" onClose={onClose}>
      <WideContent>
        <ContentItemSection>
        <SectionTitle>Content Item</SectionTitle>
        <ContentItemRow>
          <ImageBadge 
            image={contentItem.image} 
            style={{ width: '40px', height: '40px', marginRight: '16px' }} 
            entity="set" 
          />
          <ContentItemDetails>
            <ContentItemName>{contentItem.name}</ContentItemName>
            <ContentItemType>{getTypeDisplay(contentItem.goalType)}</ContentItemType>
          </ContentItemDetails>
        </ContentItemRow>
      </ContentItemSection>

      <UsageSection>
        <UsageText>Used in {getCourseCountText(courses)}</UsageText>
      </UsageSection>

      {courses.length > 0 && (
        <TableSection>
          <Table>
            <TableHeader>
              <tr>
                <th>Course Name</th>
                <th>Actions</th>
              </tr>
            </TableHeader>
            <TableBody>
              {courses.map((course: any) => (
                <tr key={course.id}>
                  <td>{course.name}</td>
                  <td>
                    <ViewCourseLink onClick={() => window.open(`/app/nav/courses/${course.id}`, '_blank')}>
                      View Course <FontAwesomeIcon icon={faExternalLinkAlt} />
                    </ViewCourseLink>
                  </td>
                </tr>
              ))}
            </TableBody>
          </Table>
        </TableSection>
      )}
      </WideContent>
    </BaseModal>
  );
};

const WideContent = styled.div`
  min-width: 50vw;
`;

const ContentItemSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 500;
  color: ${Color.darkGray};
`;

const ContentItemRow = styled.div`
  display: flex;
  align-items: center;
`;

const ContentItemDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const ContentItemName = styled.div`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const ContentItemType = styled.div`
  font-size: 14px;
  color: ${Color.darkGray};
`;

const UsageSection = styled.div`
  margin-bottom: 24px;
`;

const UsageText = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 500;
`;

const TableSection = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  th {
    text-align: left;
    padding: 12px;
    font-size: 12px;
    font-weight: 500;
    color: ${Color.darkGray};
    text-transform: uppercase;
    border-bottom: 1px solid ${Color.lightGray};
  }
`;

const TableBody = styled.tbody`
  td {
    padding: 16px 12px;
    border-bottom: 1px solid ${Color.veryLightGray};
    font-size: 14px;
  }
`;

const ViewCourseLink = styled.button`
  background: none;
  border: none;
  color: ${Color.primaryBlue};
  cursor: pointer;
  text-decoration: none;
  font-size: inherit;
  font-family: inherit;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    text-decoration: underline;
  }
`;

